import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { candidateAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LinkedInShareButtons from '../components/LinkedInShareButtons';

const STATUS_INFO = {
  under_review: { label: 'Under Review', color: '#0076A3', bg: 'rgba(30,104,255,0.08)' },
  assessment_pending: { label: 'Assessment Pending', color: '#D97706', bg: 'rgba(245,158,11,0.08)' },
  interview_scheduled: { label: 'Interview Scheduled', color: '#F2600C', bg: 'rgba(124,58,237,0.08)' },
  offer_extended: { label: 'Offer Extended', color: '#059669', bg: 'rgba(16,185,129,0.08)' },
};

export default function ApplicationDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  const loadDetails = useCallback(async () => {
    try {
      const response = await candidateAPI.getApplicationDetails(jobId);
      setDetails(response.data);
    } catch (err) {
      toast.error('Failed to load application details');
      if (err.response?.status === 404) {
        navigate('/portal');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>
        Loading application details...
      </div>
    );
  }

  if (!details) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>
        Application not found
      </div>
    );
  }

  const statusInfo = STATUS_INFO[details.status] || STATUS_INFO.under_review;

  return (
    <div data-testid="application-detail-page">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '2rem' }}>
        <div>
          <button
            onClick={() => navigate('/portal')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              background: 'none',
              border: 'none',
              color: '#0076A3',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              padding: 0,
              marginBottom: '0.75rem'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to Applications
          </button>
          <h1 className="page-title">{details.job.title}</h1>
          <p className="page-subtitle">
            {details.job.location} • {details.job.job_type} • Applied {new Date(details.application.applied_at).toLocaleDateString()}
          </p>
        </div>
        <Badge style={{ background: statusInfo.bg, color: statusInfo.color, borderRadius: 999, fontSize: '0.875rem', padding: '0.5rem 1rem', border: 'none' }}>
          {statusInfo.label}
        </Badge>
      </div>

      {/* Job Description */}
      <Card style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: '#002A38' }}>
          Job Description
        </h3>
        <p style={{ fontSize: '0.875rem', color: '#64748B', lineHeight: 1.6, marginBottom: '1rem' }}>
          {details.job.description}
        </p>
        {details.job.required_skills.length > 0 && (
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.5rem' }}>Required Skills:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
              {details.job.required_skills.map(skill => (
                <span
                  key={skill}
                  style={{
                    padding: '0.25rem 0.625rem',
                    borderRadius: 999,
                    background: 'rgba(30,104,255,0.08)',
                    border: '1px solid rgba(30,104,255,0.15)',
                    fontSize: '0.75rem',
                    color: '#0076A3'
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Assessment Scores */}
      {details.assessments.length > 0 && (
        <Card style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>
            Assessment Scores
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {details.assessments.map(assessment => (
              <div
                key={assessment._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: '#FAF7F2',
                  borderRadius: 10,
                  border: '1px solid #ECE7DE'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38', marginBottom: '0.25rem' }}>
                    {assessment.assessment_title}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    Completed {new Date(assessment.completed_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: assessment.percentage >= 70 ? '#059669' : assessment.percentage >= 50 ? '#D97706' : '#DC2626'
                      }}
                    >
                      {assessment.percentage}%
                    </div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>
                      {assessment.score}/{assessment.max_score} points
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Interview Scores */}
      {details.interviews.length > 0 && (
        <Card style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>
            Interview Scores
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {details.interviews.map(interview => (
              <div
                key={interview._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: '#FAF7F2',
                  borderRadius: 10,
                  border: '1px solid #ECE7DE'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38', marginBottom: '0.25rem' }}>
                    {interview.interview_type} Interview
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: '0.375rem' }}>
                    Completed {new Date(interview.completed_at).toLocaleDateString()}
                  </div>
                  {interview.recommendation && (
                    <div style={{ fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic' }}>
                      {interview.recommendation}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: interview.overall_score >= 70 ? '#059669' : '#D97706'
                      }}
                    >
                      {interview.overall_score}%
                    </div>
                    {interview.overall_rating && (
                      <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>
                        {interview.overall_rating}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* BGV Documents */}
      {details.bgv_documents.length > 0 && (
        <Card style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>
            Background Verification Documents
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {details.bgv_documents.map(doc => (
              <div
                key={doc._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem',
                  background: '#FAF7F2',
                  borderRadius: 10,
                  border: '1px solid #ECE7DE'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>
                      {doc.document_type}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
                      {doc.document_name} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <Badge
                  style={{
                    background: doc.status === 'verified' ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)',
                    color: doc.status === 'verified' ? '#059669' : '#D97706',
                    borderRadius: 999,
                    fontSize: '0.6875rem',
                    border: 'none'
                  }}
                >
                  {doc.status === 'verified' ? 'Verified' : 'Pending'}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* NFT Credentials */}
      {details.nft_credentials.length > 0 && (
        <Card style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>
            Issued Credentials (NFT)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {details.nft_credentials.map(cred => {
              const readinessInfo =
                cred.readiness === 'fully_verified'
                  ? { label: 'Fully Verified', color: '#059669', bg: '#ECFDF5' }
                  : cred.readiness === 'partially_verified'
                  ? { label: 'Partially Verified', color: '#0076A3', bg: '#FFE7D9' }
                  : { label: 'Initial', color: '#D97706', bg: '#FFFBEB' };

              return (
                <div
                  key={cred._id}
                  style={{
                    padding: '1.25rem',
                    background: '#FAF7F2',
                    borderRadius: 10,
                    border: '1px solid #ECE7DE'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#002A38', marginBottom: '0.25rem' }}>
                        {cred.credential_name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
                        Issued {new Date(cred.issue_date).toLocaleDateString()} • {cred.stages_completed}/4 stages completed
                      </div>
                    </div>
                    <Badge style={{ background: readinessInfo.bg, color: readinessInfo.color, borderRadius: 999, fontSize: '0.6875rem', border: 'none' }}>
                      {readinessInfo.label}
                    </Badge>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      onClick={() => window.open(cred.share_link, '_blank')}
                      style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      View Credential
                    </Button>
                    <LinkedInShareButtons
                      url={`${window.location.origin}${cred.share_link}`}
                      title={cred.credential_name}
                      certId={cred.token_id}
                      issuedAt={cred.issue_date}
                      compact
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Recruiter Feedback */}
      {details.feedback && (details.feedback.notes || details.feedback.rejection_reason) && (
        <Card style={{ marginBottom: '1.5rem', background: 'rgba(30,104,255,0.03)', border: '1px solid rgba(30,104,255,0.15)', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>
            Recruiter Feedback
          </h3>
          {details.feedback.notes && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' }}>Notes:</div>
              <p style={{ fontSize: '0.875rem', color: '#002A38', lineHeight: 1.6 }}>{details.feedback.notes}</p>
            </div>
          )}
          {details.feedback.rejection_reason && (
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' }}>Reason:</div>
              <p style={{ fontSize: '0.875rem', color: '#002A38', lineHeight: 1.6 }}>{details.feedback.rejection_reason}</p>
            </div>
          )}
        </Card>
      )}

      {/* Empty States */}
      {details.assessments.length === 0 &&
        details.interviews.length === 0 &&
        details.bgv_documents.length === 0 &&
        details.nft_credentials.length === 0 && (
          <Card style={{ background: '#FAF7F2', border: '1px solid #ECE7DE', borderRadius: 14, padding: '3rem', textAlign: 'center' }}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#CBD5E1"
              strokeWidth="1.5"
              style={{ margin: '0 auto 1rem' }}
            >
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p style={{ fontSize: '0.9375rem', color: '#64748B', marginBottom: '0.5rem' }}>
              Your application is being reviewed
            </p>
            <p style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>
              You'll receive notifications when assessments, interviews, or other updates are available.
            </p>
          </Card>
        )}
    </div>
  );
}
